import { useMemo, useState } from 'react';
import {
  Card,
  Select,
  Tag,
  Modal,
  Input,
  message,
  Empty,
  Space,
  Avatar,
  Drawer,
  List,
  Button,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import {
  StopOutlined,
  TrophyOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Page } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { useImChannel } from '../hooks/useImChannel';
import { ALL_CELLS, BOXES, type BoxDef } from '../lib/ninebox-config';

/**
 * 九宫格 — 7 块合并版（V1.1 §6.5）
 *
 * 排版严格按设计图：
 *
 *   素质↑                    业绩→
 *   ┌──────────────┬──────────────┬──────────────┐
 *   │   3          │   2+ 核心骨干 │   1 超级明星  │
 *   │  业绩待提升   │  (HM)        │  (HH)        │
 *   │ (HL+ML 合并) ├──────────────┼──────────────┤
 *   │              │   2 中坚力量  │   2+ 核心骨干 │
 *   │              │  (MM)        │  (MH)        │
 *   ├──────────────┼──────────────┴──────────────┤
 *   │   5          │              4              │
 *   │ 素质业绩待提升│       素质待提升             │
 *   │  (LL)        │       (LM+LH 合并)          │
 *   └──────────────┴─────────────────────────────┘
 *
 * 每块显示：等级编号（大字） / 名称 / 参考占比 / 实际占比 / 头像（最多 8 个）+ 折叠数。
 *
 * 数据：grid 来自 /admin/ninebox，结构 {finalCell: list[]}，finalCell = `${quality}${perf}`
 *      （H/M/L 第一位 = 素质，第二位 = 绩效）
 *
 * 交互：
 *   - 点格子打开右侧抽屉，看格子里全部员工（合并格 = 两 cell 员工合并）
 *   - 抽屉里点「调档」开调档对话框（必填理由 → 写审计日志）
 */

interface EmployeeOption {
  id: number;
  name: string;
  empNo?: string;
  position?: string;
  deptPath?: string;
  status?: string;
}

interface ExclusionRow {
  id: number;
  reason: string;
  excludedAt: string;
  excludedBy: { id: number; name: string } | null;
  employee: { id: number; name: string; empNo: string; position: string; deptPath: string; talentPool: string };
}

interface RankingRow {
  rank?: number;
  employee: { id: number; name: string; empNo: string; position: string; deptPath: string; talentPool: string };
  weightedScore: number | null;
  status: string;
  submitAt: string | null;
  redLineFlag: boolean;
  nineBoxResult: {
    id: number;
    finalCell: string;
    adjusted: boolean;
    performanceLevel: string;
    qualityLevel: string;
    qualityStandardScore: number;
  } | null;
  excluded: boolean;
  excludedReason: string | null;
}

export function NineBoxPage() {
  const qc = useQueryClient();
  const { batches, batchId, setBatchId } = useBatches();
  const { label: imLabel } = useImChannel();
  const [adjustTarget, setAdjustTarget] = useState<any | null>(null);
  const [adjustCell, setAdjustCell] = useState<string>('');
  const [adjustReason, setAdjustReason] = useState<string>('');
  const [drawerBox, setDrawerBox] = useState<BoxDef | null>(null);

  /** Slice #10：不落格管理 Drawer */
  const [exclusionDrawerOpen, setExclusionDrawerOpen] = useState(false);
  const [addExclusionEmpId, setAddExclusionEmpId] = useState<number | undefined>();
  const [addExclusionReason, setAddExclusionReason] = useState<string>('');

  /** Slice #10：胜任力排名 · 调档 Drawer */
  const [rankingDrawerOpen, setRankingDrawerOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['ninebox', batchId],
    queryFn: () => api.get('/admin/ninebox', { params: { batchId } }),
    enabled: !!batchId,
  });

  /** Slice #10：不落格列表 */
  const { data: exclusions = [] } = useQuery<ExclusionRow[]>({
    queryKey: ['ninebox-exclusions', batchId],
    queryFn: () =>
      api.get('/admin/ninebox/exclusions', { params: { batchId } }) as Promise<ExclusionRow[]>,
    enabled: !!batchId && exclusionDrawerOpen,
  });

  /** Slice #10：胜任力排名 */
  const { data: ranking = [], isFetching: rankingLoading } = useQuery<RankingRow[]>({
    queryKey: ['competency-ranking', batchId],
    queryFn: () =>
      api.get('/admin/competency/ranking', { params: { batchId } }) as Promise<RankingRow[]>,
    enabled: !!batchId && rankingDrawerOpen,
  });

  /** 标记不落格用的员工搜索（仅在 Drawer 打开时拉） */
  const { data: empPage } = useQuery<Page<EmployeeOption>>({
    queryKey: ['ninebox-exclusion-employee-options'],
    queryFn: () =>
      api.get('/admin/employees', { params: { status: 'ACTIVE', page: 1, size: 3000 } }),
    enabled: exclusionDrawerOpen,
  });
  const employeeOptions: EmployeeOption[] = empPage?.items || [];
  const excludedEmpIds = useMemo(() => new Set(exclusions.map((e) => e.employee.id)), [exclusions]);

  const addExclusionMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/ninebox/exclusions', {
        batchId,
        empId: addExclusionEmpId,
        reason: addExclusionReason.trim(),
      }) as Promise<{ cleared: { voidedAsEvaluatee: number; voidedAsEvaluator: number } | null }>,
    onSuccess: (res) => {
      if (res?.cleared) {
        message.success(
          `已标记不落格，并作废其评价任务：被评 ${res.cleared.voidedAsEvaluatee} 条、评他人未提交 ${res.cleared.voidedAsEvaluator} 条`,
        );
      } else {
        message.success('已标记为本批次不落格');
      }
      setAddExclusionEmpId(undefined);
      setAddExclusionReason('');
      qc.invalidateQueries({ queryKey: ['ninebox-exclusions', batchId] });
      qc.invalidateQueries({ queryKey: ['competency-ranking', batchId] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '标记失败');
    },
  });

  const removeExclusionMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/admin/ninebox/exclusions/${id}`) as Promise<{
        restored: number;
        skipped: Array<{ reason: string }>;
      }>,
    onSuccess: (res) => {
      const skippedNote = res?.skipped?.length ? `，${res.skipped.length} 条无法恢复需手动处理` : '';
      message.success(
        res?.restored ? `已取消不落格，恢复评价任务 ${res.restored} 条${skippedNote}` : '已取消不落格',
      );
      qc.invalidateQueries({ queryKey: ['ninebox-exclusions', batchId] });
      qc.invalidateQueries({ queryKey: ['competency-ranking', batchId] });
    },
  });

  /** 标记前拉影响预览，弹确认框（涉及作废已提交评价，必须让 HR 看到影响面再点确认）。 */
  const handleAddExclusion = async () => {
    if (!batchId || !addExclusionEmpId) return;
    try {
      const preview = (await api.get('/admin/ninebox/exclusions/impact-preview', {
        params: { batchId, empId: addExclusionEmpId },
      })) as {
        willClearTasks: boolean;
        asEvaluatee: Array<{ roleInEval: string; evaluatorName: string; status: string }>;
        asEvaluatorPending: Array<{ roleInEval: string; evaluateeName: string }>;
        keptSubmittedAsEvaluator: Array<{ evaluateeName: string }>;
        subordinateGapLeaders: Array<{ name: string }>;
      };
      const empName = employeeOptions.find((e) => e.id === addExclusionEmpId)?.name || '该员工';
      if (!preview.willClearTasks) {
        Modal.confirm({
          title: `标记 ${empName} 不落格？`,
          content: '批次已进入核算/关闭阶段，只影响九宫格标记，不改动评价任务。',
          okText: '确认标记',
          cancelText: '再想想',
          onOk: () => addExclusionMutation.mutateAsync(),
        });
        return;
      }
      const submittedIn = preview.asEvaluatee.filter((t) => t.status === 'SUBMITTED').length;
      Modal.confirm({
        title: `标记 ${empName} 不落格并清理评价任务？`,
        width: 520,
        content: (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginBottom: 4 }}>
              将作废 <strong>别人评 TA 的任务 {preview.asEvaluatee.length} 条</strong>
              {submittedIn > 0 && (
                <Typography.Text type="danger">（其中 {submittedIn} 条已提交，作废后不再计分）</Typography.Text>
              )}
              ，<strong>TA 评别人的未提交任务 {preview.asEvaluatorPending.length} 条</strong>。
            </p>
            {preview.keptSubmittedAsEvaluator.length > 0 && (
              <p style={{ marginBottom: 4 }}>
                TA 已提交的对他人评价 {preview.keptSubmittedAsEvaluator.length} 条<strong>保留</strong>
                （属于他人盘点结果）。
              </p>
            )}
            {preview.subordinateGapLeaders.length > 0 && (
              <p style={{ marginBottom: 0 }}>
                <Typography.Text type="warning">
                  注意：{preview.subordinateGapLeaders.map((l) => l.name).join('、')} 的下属评价人将清零，
                  请在「评价关系」用闪电补抽或手动补派。
                </Typography.Text>
              </p>
            )}
          </div>
        ),
        okText: '确认标记并清理',
        okButtonProps: { danger: true },
        cancelText: '再想想',
        onOk: () => addExclusionMutation.mutateAsync(),
      });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '获取影响预览失败');
    }
  };

  /** 取消前拉恢复预览，弹确认框。 */
  const handleRemoveExclusion = async (row: ExclusionRow) => {
    try {
      const preview = (await api.get(`/admin/ninebox/exclusions/${row.id}/restore-preview`)) as {
        willRestoreTasks: boolean;
        restorable: Array<{ roleInEval: string; evaluatorName: string; evaluateeName: string }>;
        skipped: Array<{ evaluatorName: string; evaluateeName: string; reason: string }>;
      };
      Modal.confirm({
        title: `取消 ${row.employee.name} 的「不落格」标记？`,
        width: 520,
        content: (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginBottom: 4 }}>下次核算时该员工会回到九宫格里。</p>
            {preview.willRestoreTasks ? (
              <>
                <p style={{ marginBottom: 4 }}>
                  将恢复因不落格作废的评价任务 <strong>{preview.restorable.length} 条</strong>
                  （原评价人不变，已提交的分数一并恢复）。
                </p>
                {preview.skipped.length > 0 && (
                  <p style={{ marginBottom: 0 }}>
                    <Typography.Text type="warning">
                      {preview.skipped.length} 条无法恢复：
                      {preview.skipped
                        .map((s) => `${s.evaluatorName}→${s.evaluateeName}（${s.reason}）`)
                        .join('；')}
                    </Typography.Text>
                  </p>
                )}
              </>
            ) : (
              <p style={{ marginBottom: 0 }}>批次已进入核算/关闭阶段，评价任务不做改动。</p>
            )}
          </div>
        ),
        okText: '取消标记',
        cancelText: '保留',
        onOk: () => removeExclusionMutation.mutateAsync(row.id),
      });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '获取恢复预览失败');
    }
  };

  const recentHireSince: string | null = (data as any)?.recentHireSince ?? null;
  const excludedRecentHireCount: number = (data as any)?.excludedRecentHireCount ?? 0;

  const adjustMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/ninebox/${adjustTarget.id}/adjust`, {
        finalCell: adjustCell,
        reason: adjustReason,
      }),
    onSuccess: () => {
      message.success('调档完成，已写入审计日志');
      setAdjustTarget(null);
      setAdjustReason('');
      qc.invalidateQueries({ queryKey: ['ninebox', batchId] });
      qc.invalidateQueries({ queryKey: ['competency-ranking', batchId] });
    },
  });

  const grid: Record<string, any[]> = (data as any)?.grid || {};

  const total = useMemo(
    () =>
      ALL_CELLS.reduce((s, c) => s + (grid[c]?.length || 0), 0),
    [grid],
  );

  const drawerEmps = useMemo(() => {
    if (!drawerBox) return [];
    return drawerBox.cells.flatMap((c) => grid[c] || []);
  }, [drawerBox, grid]);

  return (
    <div>
      <div className="tg-page-title">九宫格</div>
      <Card>
        <Space style={{ marginBottom: 8 }} wrap>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            value={batchId}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
          <Tag color="purple">仅管理员可见</Tag>
          <Tag color="blue">老员工核心盘 {total} 人</Tag>
          {excludedRecentHireCount > 0 && (
            <Tag color="orange">
              已剔除近期入职新人 {excludedRecentHireCount} 人（不入九宫格）
            </Tag>
          )}
          <Button
            icon={<StopOutlined />}
            onClick={() => setExclusionDrawerOpen(true)}
            disabled={!batchId}
          >
            不落格管理
          </Button>
          <Button
            icon={<TrophyOutlined />}
            type="primary"
            onClick={() => setRankingDrawerOpen(true)}
            disabled={!batchId}
          >
            胜任力排名 · 调档
          </Button>
        </Space>
        {recentHireSince && (
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            统计口径：剔除 {new Date(recentHireSince).toISOString().slice(0, 10)} 起入职的新人
            （入职窗口期不足以稳定反映绩效，对应人才盘点报告 §2 / §3.1 口径）
          </div>
        )}

        {!batchId ? (
          <Empty description="请选择批次" />
        ) : (
          <NineBoxLayout
            grid={grid}
            total={total}
            onBoxClick={(b) => setDrawerBox(b)}
          />
        )}
      </Card>

      <Drawer
        title={
          drawerBox ? `${drawerBox.name}（${drawerEmps.length} 人）` : ''
        }
        open={!!drawerBox}
        onClose={() => setDrawerBox(null)}
        width={460}
      >
        {drawerBox && (
          <List
            dataSource={drawerEmps}
            locale={{ emptyText: '该格无人' }}
            renderItem={(item: any) => (
              <List.Item
                actions={[
                  <a
                    key="adjust"
                    onClick={() => {
                      setAdjustTarget(item);
                      setAdjustCell(item.finalCell);
                      setDrawerBox(null);
                    }}
                  >
                    调档
                  </a>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar src={item.employee.avatar}>
                      {item.employee.name?.[0]}
                    </Avatar>
                  }
                  title={
                    <Space>
                      {item.employee.name}
                      <Tag>{item.finalCell}</Tag>
                      {item.adjusted && <Tag color="orange">已调</Tag>}
                    </Space>
                  }
                  description={
                    <span style={{ fontSize: 12, color: '#888' }}>
                      {item.employee.position} · {item.employee.deptPath} · 素质分{' '}
                      {item.qualityStandardScore}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Modal
        title={adjustTarget ? `调档 · ${adjustTarget.employee.name}` : ''}
        open={!!adjustTarget}
        onOk={() => {
          if (!adjustReason.trim()) {
            message.error('必须填调档理由');
            return;
          }
          adjustMutation.mutate();
        }}
        confirmLoading={adjustMutation.isPending}
        onCancel={() => setAdjustTarget(null)}
      >
        <p>
          当前格：<Tag>{adjustTarget?.finalCell}</Tag>
        </p>
        <p>
          调到：
          <Select
            value={adjustCell}
            style={{ width: 120 }}
            onChange={setAdjustCell}
            options={ALL_CELLS.map((c) => ({ label: c, value: c }))}
          />
        </p>
        <p style={{ marginTop: 12 }}>调档理由（必填）：</p>
        <Input.TextArea
          value={adjustReason}
          onChange={(e) => setAdjustReason(e.target.value)}
          rows={3}
          placeholder="如：经合议会一致同意，张三本期实际表现优于素质分反映..."
        />
      </Modal>

      {/* === Slice #10：不落格管理 Drawer === */}
      <Drawer
        title={`不落格管理 · ${batches.find((b) => b.id === batchId)?.name || '本批次'}`}
        open={exclusionDrawerOpen}
        onClose={() => setExclusionDrawerOpen(false)}
        width={560}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          标记为「不落格」的员工在<strong>本批次的九宫格核算</strong>会被跳过，已存在的旧落格在重算时清除。
          批次未核算前标记，还会<strong>同步作废其评价任务</strong>（别人评 TA 的全部 + TA 评别人的未提交部分）；
          取消标记时这些任务会原样恢复。适用于：试用未通过、本期临时调离、已离职但未及时同步{imLabel}等场景。
          <strong>仅对本批次生效</strong>；其他批次不受影响。
        </Typography.Paragraph>

        <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            <PlusOutlined /> 标记一名员工不落格
          </Typography.Title>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Select
              showSearch
              allowClear
              placeholder="搜索员工姓名 / 工号 / 部门 / 职位"
              value={addExclusionEmpId}
              onChange={setAddExclusionEmpId}
              style={{ width: '100%' }}
              filterOption={(input, opt: any) => {
                const kw = input.toLowerCase();
                return ((opt?.search || '') as string).toLowerCase().includes(kw);
              }}
              options={employeeOptions.map((e) => ({
                value: e.id,
                label: (
                  <span>
                    {e.name}
                    {excludedEmpIds.has(e.id) && (
                      <Tag color="orange" style={{ marginLeft: 6 }}>
                        已不落格
                      </Tag>
                    )}
                    <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                      {e.empNo || ''} · {e.position || ''} · {e.deptPath || ''}
                    </Typography.Text>
                  </span>
                ),
                search: `${e.name} ${e.empNo || ''} ${e.position || ''} ${e.deptPath || ''}`,
                disabled: excludedEmpIds.has(e.id),
              }))}
            />
            <Input.TextArea
              rows={2}
              placeholder="必填：不落格原因（如「试用期未通过，6 月底主动离职」「7 月调岗，本期不参与盘点」）"
              value={addExclusionReason}
              onChange={(e) => setAddExclusionReason(e.target.value)}
              maxLength={200}
              showCount
            />
            <Button
              type="primary"
              icon={<StopOutlined />}
              onClick={handleAddExclusion}
              loading={addExclusionMutation.isPending}
              disabled={!addExclusionEmpId || !addExclusionReason.trim()}
              block
            >
              确认标记不落格
            </Button>
          </Space>
        </Card>

        <Typography.Title level={5} style={{ marginTop: 16 }}>
          本批次已标记不落格（{exclusions.length} 人）
        </Typography.Title>
        {exclusions.length === 0 ? (
          <Empty description="尚未标记任何不落格员工" />
        ) : (
          <List
            dataSource={exclusions}
            renderItem={(r) => (
              <List.Item
                actions={[
                  <a key="remove" style={{ color: '#fa541c' }} onClick={() => handleRemoveExclusion(r)}>
                    取消
                  </a>,
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar style={{ background: '#fa8c16' }}>{r.employee.name?.[0]}</Avatar>}
                  title={
                    <Space size={6}>
                      <strong>{r.employee.name}</strong>
                      <Tag>{r.employee.empNo}</Tag>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {r.employee.position} · {r.employee.deptPath}
                      </Typography.Text>
                    </Space>
                  }
                  description={
                    <span style={{ fontSize: 12, color: '#666' }}>
                      原因：{r.reason}
                      {r.excludedBy && (
                        <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                          · 操作人 {r.excludedBy.name} · {new Date(r.excludedAt).toLocaleString('zh-CN', { hour12: false })}
                        </Typography.Text>
                      )}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      {/* === Slice #10：胜任力排名 · 调档 Drawer === */}
      <Drawer
        title={`胜任力排名（按加权分降序）· ${batches.find((b) => b.id === batchId)?.name || '本批次'}`}
        open={rankingDrawerOpen}
        onClose={() => setRankingDrawerOpen(false)}
        width={1100}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          按当前批次胜任力加权分（领导评 5 维 × 5 档锚定）从高到低排序。
          <strong>「调档」</strong>会更新员工在九宫格的最终落格（必填理由），与现有九宫格调档同口径写审计日志。
          <strong>未提交评价</strong>的员工排在末尾、无加权分；<strong>未核算落格</strong>的员工无法调档（请先在批次核算后再来）。
        </Typography.Paragraph>
        <Table<RankingRow>
          rowKey={(r) => r.employee.id}
          loading={rankingLoading}
          dataSource={ranking}
          size="middle"
          pagination={{ pageSize: 30, showSizeChanger: true, showTotal: (t) => `共 ${t} 人` }}
          columns={[
            {
              title: '名次',
              key: 'rank',
              width: 60,
              render: (_v, _r, idx) => <Tag color={idx < 3 ? 'gold' : 'default'}>{idx + 1}</Tag>,
            },
            {
              title: '员工',
              key: 'name',
              width: 140,
              render: (_v, r) => (
                <Space size={6} align="start">
                  <Avatar style={{ background: '#722ed1' }}>{r.employee.name?.[0]}</Avatar>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.employee.name}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {r.employee.empNo}
                    </Typography.Text>
                  </div>
                </Space>
              ),
            },
            { title: '职位', dataIndex: ['employee', 'position'], width: 110, ellipsis: true },
            { title: '部门', dataIndex: ['employee', 'deptPath'], ellipsis: true },
            {
              title: '加权分',
              dataIndex: 'weightedScore',
              align: 'right' as const,
              width: 90,
              render: (v: number | null) =>
                v == null ? <Typography.Text type="secondary">—</Typography.Text> : <strong>{Number(v).toFixed(2)}</strong>,
            },
            {
              title: '状态',
              dataIndex: 'status',
              align: 'center' as const,
              width: 90,
              render: (v) =>
                v === 'SUBMITTED' ? <Tag color="success">已提交</Tag> : <Tag color="warning">待提交</Tag>,
            },
            {
              title: '红线',
              dataIndex: 'redLineFlag',
              align: 'center' as const,
              width: 70,
              render: (v) => (v ? <Tag color="error">触发</Tag> : <Tag>—</Tag>),
            },
            {
              title: '九宫格',
              dataIndex: 'nineBoxResult',
              align: 'center' as const,
              width: 100,
              render: (n: RankingRow['nineBoxResult'], r) =>
                r.excluded ? (
                  <Tooltip title={r.excludedReason || ''}>
                    <Tag color="orange">不落格</Tag>
                  </Tooltip>
                ) : n ? (
                  <Space size={4}>
                    <Tag color={n.adjusted ? 'orange' : 'blue'}>{n.finalCell}</Tag>
                    {n.adjusted && <Typography.Text type="secondary" style={{ fontSize: 11 }}>已调</Typography.Text>}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">未核算</Typography.Text>
                ),
            },
            {
              title: '操作',
              key: 'op',
              width: 110,
              fixed: 'right' as const,
              render: (_v, r) =>
                r.excluded ? (
                  <Typography.Text type="secondary">不落格中</Typography.Text>
                ) : r.nineBoxResult ? (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setAdjustTarget({
                        id: r.nineBoxResult!.id,
                        finalCell: r.nineBoxResult!.finalCell,
                        employee: r.employee,
                      });
                      setAdjustCell(r.nineBoxResult!.finalCell);
                      setAdjustReason('');
                    }}
                  >
                    调档
                  </Button>
                ) : (
                  <Tooltip title="该员工尚未在本批次核算落格。请先到「核算」触发批次核算">
                    <Button size="small" disabled>
                      调档
                    </Button>
                  </Tooltip>
                ),
            },
          ]}
          scroll={{ x: 'max-content' }}
        />
      </Drawer>
    </div>
  );
}

interface LayoutProps {
  grid: Record<string, any[]>;
  total: number;
  onBoxClick: (box: BoxDef) => void;
}

function NineBoxLayout({ grid, total, onBoxClick }: LayoutProps) {
  const Y_LABELS = ['高', '中', '低'];
  const X_LABELS = ['低', '中', '高'];

  return (
    <div style={{ padding: '12px 8px 8px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '64px 1fr',
          gridTemplateRows: '1fr 56px',
          alignItems: 'stretch',
        }}
      >
        {/* 左侧 Y 轴标签：素质 + 高/中/低 */}
        <div
          style={{
            gridColumn: '1 / 2',
            gridRow: '1 / 2',
            display: 'grid',
            gridTemplateRows: 'repeat(3, 1fr)',
            position: 'relative',
            paddingRight: 8,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              writingMode: 'vertical-rl',
              fontSize: 14,
              color: '#666',
              letterSpacing: 4,
            }}
          >
            素 质
          </div>
          {Y_LABELS.map((l) => (
            <div
              key={l}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontSize: 13,
                color: '#666',
                paddingRight: 4,
              }}
            >
              {l}
            </div>
          ))}
        </div>

        {/* 主网格 3×3 + 7 个 box */}
        <div
          style={{
            gridColumn: '2 / 3',
            gridRow: '1 / 2',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(3, 150px)',
            gap: 12,
          }}
        >
          {BOXES.map((box) => {
            const list = box.cells.flatMap((c) => grid[c] || []);
            const n = list.length;
            const actualPct = total > 0 ? (n / total) * 100 : 0;
            return (
              <BoxCell
                key={box.id}
                box={box}
                count={n}
                actualPct={actualPct}
                employees={list}
                onClick={() => onBoxClick(box)}
              />
            );
          })}
        </div>

        {/* 底部 X 轴标签：业绩 + 低/中/高 */}
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }} />
        <div
          style={{
            gridColumn: '2 / 3',
            gridRow: '2 / 3',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingTop: 6,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}
          >
            {X_LABELS.map((l) => (
              <div
                key={l}
                style={{
                  textAlign: 'center',
                  fontSize: 13,
                  color: '#666',
                }}
              >
                {l}
              </div>
            ))}
          </div>
          <div
            style={{
              textAlign: 'center',
              fontSize: 14,
              color: '#666',
              marginTop: 4,
              letterSpacing: 4,
            }}
          >
            业 绩
          </div>
        </div>
      </div>
    </div>
  );
}

interface BoxCellProps {
  box: BoxDef;
  count: number;
  actualPct: number;
  employees: any[];
  onClick: () => void;
}

function BoxCell({ box, count, actualPct, employees, onClick }: BoxCellProps) {
  const visibleAvatars = employees.slice(0, 8);
  const overflow = Math.max(0, employees.length - visibleAvatars.length);
  return (
    <div
      onClick={onClick}
      style={{
        gridArea: box.gridArea,
        background: box.bg,
        color: '#fff',
        borderRadius: 12,
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'transform 0.12s, box-shadow 0.12s',
        userSelect: 'none',
        position: 'relative',
        textAlign: 'center',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(-2px)';
        el.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(0)';
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.25,
          opacity: 0.98,
        }}
      >
        {box.name}
      </div>
      <div style={{ fontSize: 12, marginTop: 8, opacity: 0.92 }}>
        参考：{box.ref}
      </div>
      <div style={{ fontSize: 12, marginTop: 2, opacity: 0.92 }}>
        实际：{actualPct.toFixed(2)}%（{count} 人）
      </div>
      {employees.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4,
            marginTop: 10,
          }}
        >
          {visibleAvatars.map((item) => (
            <Avatar
              key={item.id}
              size={22}
              src={item.employee?.avatar}
              style={{
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                fontSize: 11,
              }}
            >
              {item.employee?.name?.[0]}
            </Avatar>
          ))}
          {overflow > 0 && (
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              +{overflow}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
